import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { AutomationFolder } from '@/types/automation';

interface CreateFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    name: string;
    description?: string;
    color?: string;
  }) => void;
  editingFolder?: AutomationFolder | null;
}

const PRESET_COLORS = [
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Gray', value: '#6B7280' },
];

export function CreateFolderDialog({
  open,
  onOpenChange,
  onSubmit,
  editingFolder
}: CreateFolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string | undefined>(undefined);

  const isEditing = !!editingFolder;

  // Reset or populate form when dialog opens/closes or editing folder changes
  useEffect(() => {
    if (open && editingFolder) {
      setName(editingFolder.name);
      setDescription(editingFolder.description || '');
      setColor(editingFolder.color || undefined);
    } else if (!open) {
      // Reset on close
      setName('');
      setDescription('');
      setColor(undefined);
    }
  }, [open, editingFolder]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      color: color || undefined
    });

    // Reset form
    setName('');
    setDescription('');
    setColor(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Folder' : 'Create Folder'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the folder details below.'
                : 'Create a new folder to organize your automations.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Folder Name */}
            <div className="space-y-2">
              <Label htmlFor="folder-name">
                Folder Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="folder-name"
                placeholder="e.g., Onboarding, SLA Reminders, Documents"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </div>

            {/* Folder Description */}
            <div className="space-y-2">
              <Label htmlFor="folder-description">
                Description <span className="text-muted-foreground text-xs">(Optional)</span>
              </Label>
              <Textarea
                id="folder-description"
                placeholder="Brief description of this folder's purpose"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>

            {/* Folder Color */}
            <div className="space-y-2">
              <Label>
                Folder Color <span className="text-muted-foreground text-xs">(Optional)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {/* No Color Option */}
                <button
                  type="button"
                  className={`w-8 h-8 rounded border-2 flex items-center justify-center transition-all ${
                    color === undefined
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setColor(undefined)}
                  title="No color"
                >
                  <div className="w-6 h-6 rounded bg-muted" />
                </button>

                {/* Preset Colors */}
                {PRESET_COLORS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`w-8 h-8 rounded border-2 transition-all ${
                      color === preset.value
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setColor(preset.value)}
                    title={preset.name}
                  >
                    <div
                      className="w-full h-full rounded"
                      style={{ backgroundColor: preset.value }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              {isEditing ? 'Save Changes' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
